import { rem } from "@certn/cascadia";
import {
  ColumnDefinition,
  DataTable,
  GhostIconButton,
  Icon,
  NeutralTag,
  SuccessTag,
  Text,
} from "@certn/cascadia-react";
import { css } from "@emotion/css";
import { SortDescriptor } from "@react-types/shared";
import { t, TFunction } from "i18next";
import { ResizableTableContainer } from "react-aria-components";
import { useNavigate } from "react-router-dom";

import { GroupDropdownMenu } from "./GroupDropdownMenu";
import { Schemas } from "../../../types";
import { useClientAccount } from "../../../hooks/useClientAccount";

export function GroupsTable(props: {
  onSortChange: (sortDescriptor: SortDescriptor) => void;
  sortDescriptor?: SortDescriptor;
  results: Schemas["CustomerGroupList"][] | undefined;
  isLoading: boolean;
  mutate: () => Promise<unknown>;
  onFavouriteClick: (
    id: string,
    isFavourite: boolean,
    t: TFunction,
    mutate: () => void
  ) => void;
}) {
  const {
    onSortChange,
    sortDescriptor,
    results,
    isLoading,
    mutate,
    onFavouriteClick,
  } = props;
  const navigate = useNavigate();
  const { client } = useClientAccount();

  const columns: ColumnDefinition<Schemas["CustomerGroupList"]>[] = [
    {
      name: (
        <Icon
          className={styles.iconSize}
          name="star"
          color="icon-weak"
          padding="space-1-5"
          fill
        />
      ),
      key: "is_favourite",
      isRowHeader: true,
      width: "1%",
      render: (customerGroup) => {
        return (
          <GhostIconButton
            testID="group-table-favourite-button"
            color="icon-base"
            icon="star"
            onPress={() =>
              onFavouriteClick(
                customerGroup.id,
                customerGroup.is_favourite,
                t,
                mutate
              )
            }
            size="small"
            aria-label={t("Favourite")}
            iconFill={customerGroup.is_favourite}
          />
        );
      },
    },
    {
      name: t("Group name"),
      key: "name",
      isRowHeader: true,
      allowsSorting: true,
      render: (customerGroup) => {
        return (
          <>
            <div>
              <Text style="global/body-secondary">
                {customerGroup?.name ?? "--"}
              </Text>
            </div>
            {customerGroup.user_count && (
              <div>
                <Text style="global/footer-small" color="text-weak">
                  {customerGroup.user_count}{" "}
                  {customerGroup.user_count === 1 ? t("member") : t("members")}
                </Text>
              </div>
            )}
          </>
        );
      },
    },
    {
      name: t("Applicant facing name"),
      key: "applicant_facing_name",
      render: (customerGroup) => customerGroup?.applicant_facing_name ?? "--",
      allowsSorting: true,
    },
    {
      name: t("Billing profile"),
      key: "billing_profile__profile_name",
      render: (customerGroup) =>
        customerGroup?.billing_profile?.profile_name ?? "--",
      allowsSorting: true,
    },
    ...(client?.applicant_pay_enabled
      ? [
          {
            name: t("Payer"),
            key: "payer",
            render: (customerGroup) => {
              const payerOption = customerGroup?.billing_profile?.payer_option;
              if (payerOption === "APPLICANT") return t("Applicant");
              if (payerOption === "CLIENT") return t("Client");
              return "--";
            },
          } as ColumnDefinition<Schemas["CustomerGroupList"]>,
        ]
      : []),
    {
      name: t("Status"),
      key: "is_active",
      render: (customerGroup) =>
        customerGroup?.is_active ? (
          <SuccessTag type="subtle">{t("Active")}</SuccessTag>
        ) : (
          <NeutralTag type="subtle">{t("Deactivated")}</NeutralTag>
        ),
      allowsSorting: true,
    },
    {
      key: "menu",
      isAction: true,
      render: (group) => {
        return (
          <GroupDropdownMenu
            mutate={mutate}
            groupId={group?.id}
            groupName={group?.name}
            isActive={group?.is_active}
          />
        );
      },
    },
  ];

  return (
    <ResizableTableContainer>
      <DataTable
        columns={columns}
        onRowAction={(id) => {
          navigate(`/group-overview?customer_group_id=${id}`, {
            state: {
              last: { href: `${location.pathname}${location.search}` },
            },
          });
        }}
        rows={results || []}
        isLoading={isLoading}
        renderEmptyState={() => <div>{t("No groups")}</div>}
        aria-label="customer-groups-table"
        onSortChange={onSortChange}
        sortDescriptor={sortDescriptor}
      />
    </ResizableTableContainer>
  );
}

const styles = {
  favouriteIcon: css({
    display: "flex",
    justifyContent: "left",
    alignItems: "left",
  }),
  iconSize: css({
    width: rem("space-5"),
    height: rem("space-5"),
    marginLeft: "-3px",
  }),
};
